import React, { useState, useEffect, useCallback } from 'react';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import { useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import TokenService from '../../services/autentication/token.service';
import SalesOrderingService from '../../services/sales-ordering/sales-ordering-service';
import orderServices from '../../services/orders/orderservice';
import editUserServices from '../../services/edit-users/edit-users.service';
import { UserDTO, UserAddressDTO, SalesOrderingCustomerProps, UserOption } from '../../models/sales-ordering/sales-ordering-model';
import './sales-ordering.css';

const SalesOrderingCustomer: React.FC<SalesOrderingCustomerProps> = ({
    visible,
    onHide,
    onCustomerCreated,
    onAddAddress,
    toast,
    selectedUserId,
    selectedUser,
    selectedAddress,
    userAddresses: propUserAddresses,
    isSelfPickup = false,
    onUserSelect,
    onAddressSelect,
    onAddCustomer,
    showUserSelection = false
}) => {
    const [loading, setLoading] = useState(false);

    const formik: any = useFormik({
        initialValues: {
            name: '',
            mobile: ''
        },
        validate: (data: any) => {
            let errors: any = {};

            if (!data.name) {
                errors.name = "Name is required";
            } else if (data.name.startsWith(' ')) {
                errors.name = "Name should not start with a space";
            } else if (data.name.includes('  ')) {
                errors.name = "Multiple spaces are not allowed";
            }

            if (!data.mobile) {
                errors.mobile = "Mobile number is required";
            } else if (!/^[6-9]\d{9}$/.test(data.mobile)) {
                errors.mobile = "Mobile number must be 10 digits and start with 6, 7, 8, or 9";
            }

            return errors;
        },
        onSubmit: async (values: any) => {
            try {
                setLoading(true);
                const currentUser = TokenService.getUser();
                const currentUserId = currentUser?.userID || currentUser?.userId || currentUser?.id || 1;

                const result = await SalesOrderingService.createCustomer({
                    name: values.name.trim(),
                    mobile: values.mobile.trim(),
                    createdBy: currentUserId
                });

                if (result.success || result.userId || result.userID) {
                    toast.current?.show({ severity: 'success', summary: 'Customer Created', detail: 'Customer created successfully' });

                    const newId = result.userId || result.userID;

                    onCustomerCreated(newId, values.name, values.mobile);

                    formik.resetForm();
                    onHide();
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to create customer' });
                }
            } catch (error: any) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'An error occurred' });
            } finally {
                setLoading(false);
            }
        }
    });

    const isFormFieldInvalid = (name: string) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name: string) => {
        return isFormFieldInvalid(name) ? <small className="p-error block mt-1">{formik.errors[name]}</small> : "";
    };

    const [selectedUserText, setSelectedUserText] = useState<string>('');
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [internalAddresses, setInternalAddresses] = useState<UserAddressDTO[]>([]);

    const addressesToDisplay = propUserAddresses || internalAddresses;

    // Restore the autocomplete display text when navigating back (e.g. from payment step)
    // Or when a new customer is explicitly passed in from parent state
    useEffect(() => {
        if (selectedUserId && selectedUser) {
            setSelectedUserText(`${selectedUser.name}, ${selectedUser.mobile}`);
        } else if (!selectedUserId) {
            setSelectedUserText('');
        }
    }, [selectedUserId, selectedUser]);

    useEffect(() => {
        const loadAddresses = async () => {
            if (selectedUserId && showUserSelection && !propUserAddresses) {
                setLoading(true);
                try {
                    const addresses = await SalesOrderingService.getUserAddresses(selectedUserId);
                    setInternalAddresses(addresses);
                    if (addresses.length > 0 && !selectedAddress && onAddressSelect) {
                        onAddressSelect(addresses[0]);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load addresses'
                    });
                } finally {
                    setLoading(false);
                }
            } else if (!selectedUserId) {
                setInternalAddresses([]);
            }
        };
        loadAddresses();
    }, [selectedUserId, showUserSelection, propUserAddresses, onAddressSelect, toast]);

    const handleUserSearch = useCallback((event: AutoCompleteCompleteEvent) => {
        const query = event.query;
        if (query && query.length >= 3) {
            const lowerQuery = query.toLowerCase();
            const searchFilter = `?$filter=true and (startswith(tolower(name), '${lowerQuery}') or startswith(tolower(primaryContactNbr), '${lowerQuery}'))`;
            editUserServices.getUsers(searchFilter).then((response: any[]) => {
                if (response && response.length > 0) {
                    const activeUsers = response.filter(u => !u.isDeleted);
                    const mapped = activeUsers.map(u => ({
                        ...u,
                        userID: u.userID || u.userId || u.id,
                        detail: `${u.name}, ${u.primaryContactNbr}`
                    }));
                    setUserOptions(mapped);
                } else {
                    setUserOptions([]);
                }
            }).catch(error => {
                setUserOptions([]);
            });
        } else {
            setUserOptions([]);
        }
    }, []);

    const handleUserSelectInternal = useCallback((e: any) => {
        const selected = e.value;
        if (selected && typeof selected === 'object' && onUserSelect) {
            setSelectedUserText(`${selected.name}, ${selected.primaryContactNbr}`);
            onUserSelect({
                userId: selected.userID,
                name: selected.name,
                mobile: selected.primaryContactNbr
            });
        }
    }, [onUserSelect]);

    const handleCancel = useCallback(() => {
        formik.resetForm();
        onHide();
    }, [onHide, formik]);

    if (showUserSelection) {
        return (
            <div className="sales-section-card">
                <div className="sales-section-header">
                    <span><i className="pi pi-user" /> {isSelfPickup ? 'Select Customer' : 'Delivery Address'}</span>
                    <Button
                        label="Add"
                        icon="pi pi-user-plus"
                        className="p-button-text p-button-sm p-0 text-green-500"
                        onClick={() => {
                            if (onAddCustomer) onAddCustomer();
                        }}
                    />
                </div>
                <div className="sales-section-body">
                    <AutoComplete
                        value={selectedUserText}
                        suggestions={userOptions}
                        completeMethod={handleUserSearch}
                        field="detail"
                        onChange={(e) => {
                            setSelectedUserText(e.value);
                            if (!e.value || (typeof e.value === 'string' && e.value.trim() === '')) {
                                onUserSelect && onUserSelect({ userId: 0, name: '', mobile: '' });
                            }
                        }}
                        onSelect={handleUserSelectInternal}
                        placeholder="Type name or number..."
                        className="w-full mb-3 border-1"
                        inputClassName="w-full search-input"
                        minLength={3}
                        dropdown
                    />

                    {!isSelfPickup && !!selectedUserId && addressesToDisplay.length > 0 && (
                        <div className="flex flex-column gap-2">
                            {addressesToDisplay.map(addr => (
                                <div
                                    key={addr.addressId}
                                    className={`sales-user-card ${selectedAddress?.addressId === addr.addressId ? 'selected' : ''}`}
                                    onClick={() => onAddressSelect && onAddressSelect(addr)}
                                >
                                    <RadioButton
                                        inputId={`addr-${addr.addressId}`}
                                        name="address"
                                        value={addr}
                                        onChange={(e) => onAddressSelect && onAddressSelect(e.value)}
                                        checked={selectedAddress?.addressId === addr.addressId}
                                    />
                                    <label htmlFor={`addr-${addr.addressId}`} className="ml-2 cursor-pointer flex-1">
                                        <div className="font-bold text-sm">{addr.addressLine1}</div>
                                        <div className="text-xs text-gray-600">
                                            {addr.addressLine2} {addr.city} {addr.state} {addr.pincode ? `Pin-${addr.pincode}` : ''}
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}

                    {!isSelfPickup && !!selectedUserId && (
                        <Button
                            label="Add New Address"
                            icon="pi pi-map-marker"
                            className="p-button-outlined p-button-sm mt-2 p-button-help"
                            style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem', maxWidth: '200px' }}
                            onClick={onAddAddress}
                        />
                    )}

                </div>
            </div>
        );
    }

    if (!visible) return null;

    return (
        <div className="pt-2 customer-add-form">
            <div className="mb-2">
                <label className="block mb-1 text-sm">Name *</label>
                <InputText
                    id="name"
                    name="name"
                    className={classNames('w-full border-1 border-solid p-2', { 'p-invalid': isFormFieldInvalid('name'), 'border-300': !isFormFieldInvalid('name') })}
                    placeholder="Enter name"
                    value={formik.values.name}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val.startsWith(' ')) return;
                        if (val.includes('  ')) return;
                        formik.setFieldValue('name', val);
                    }}
                    onBlur={formik.handleBlur}
                />
                {getFormErrorMessage('name')}
            </div>
            <div className="mb-2">
                <label className="block mb-1 text-sm">Mobile *</label>
                <InputText
                    id="mobile"
                    name="mobile"
                    className={classNames('w-full border-1 border-solid p-2', { 'p-invalid': isFormFieldInvalid('mobile'), 'border-300': !isFormFieldInvalid('mobile') })}
                    placeholder="Enter mobile"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    keyfilter="pint"
                    maxLength={10}
                />
                {getFormErrorMessage('mobile')}
            </div>
            <div className="flex gap-2 flex-column mt-4">
                <div className="flex gap-2">
                    <Button
                        type="button"
                        label="Cancel"
                        className="p-button-outlined p-button-sm flex-1"
                        onClick={handleCancel}
                    />
                    <Button
                        type="button"
                        label="Save"
                        className="p-button-success p-button-sm flex-1"
                        onClick={() => formik.handleSubmit()}
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default SalesOrderingCustomer;
